import { Language } from 'types/language'
import { Address } from '@boltenergy-be/shared'
import { AddressFormats } from 'types/formatting'

/**
 * Capitalizes the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const capitalize = (text?: string | null): string | undefined => {
  if (!text) return ''
  return text.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase())
}

/**
 * Formats a number to the localized Belgian number notation with the optional max given fraction digits (default 2)
 *
 * @param {number} num
 * @param {Language} language - Default: Language.DUTCH
 * @param {number} maximumFractionDigits
 * @returns {string}
 * @example formatAmount(1234.5, Language.DUTCH) // '1.234,5'
 * @example formatAmount(1234.5, Language.FRENCH) // '1 234,5'
 */
export const formatAmount = (
  num: number | undefined,
  language: Language = Language.DUTCH,
  maximumFractionDigits = 2
): string | undefined => {
  return num?.toLocaleString(`${language}-BE`, { maximumFractionDigits })
}

/**
 * Formats a number as a localized EUR price for the Belgian market with the optional max given fraction digits (default 2)
 *
 * @param {number} amount
 * @param {Language} language - Default: Language.DUTCH
 * @param {number} maximumFractionDigits
 * @returns {string}
 * @example formatPrice(1234.5, Language.DUTCH) // '€ 1.234,50'
 * @example formatPrice(1234.5, Language.FRENCH) // '1 234,50 €'
 */
export const formatPrice = (amount: number, language: Language = Language.DUTCH, maximumFractionDigits = 2): string => {
  return new Intl.NumberFormat(`${language}-BE`, {
    style: 'currency',
    currency: 'EUR',
    // Older engines throw a RangeError when max drops below the currency's default min (2 for EUR)
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits
  }).format(amount)
}

/**
 * Removes all special characters and converts to lowercase
 *
 * @param {string} text
 * @returns {string}
 */
export const normalizeText = (text: string): string => {
  return text
    ? text
        .normalize('NFD') // separate accents from letters and return Unicode codes
        .replace(/[\u0300-\u036f]/g, '') // replace diacritical characters with empty string
        .toLowerCase()
        .trim()
    : ''
}

/**
 * Formats the given address into a string
 *
 * @param {Address} address
 * @param {AddressFormats | undefined} format - Default: AddressFormats.FULL_SINGLE_LINE
 * @returns string|null
 */
export const formatAddress = (
  address?: Omit<Address, 'postalCode'> & { postalCode: number | string },
  format: AddressFormats | undefined = AddressFormats.FULL_SINGLE_LINE
): string | null => {
  if (!address || Object.values(address).every((value) => !value)) {
    return null
  }

  const lowercaseStreetName = capitalize(address.streetName)
  const lowercaseTownName = capitalize(address.townName)
  const lineOne = address.streetBox
    ? `${lowercaseStreetName} ${address.streetNumber} ${address.streetBox}`
    : `${lowercaseStreetName} ${address.streetNumber}`
  const lineTwo = `${address.postalCode} ${lowercaseTownName}`

  switch (format) {
    case AddressFormats.FULL_TWO_LINES: {
      return `${lineOne}<br />${lineTwo}`
    }
    case AddressFormats.LINE_ONE: {
      return lineOne
    }
    case AddressFormats.LINE_TWO: {
      return lineTwo
    }

    case AddressFormats.FULL_SINGLE_LINE:
    default: {
      return `${lineOne}, ${lineTwo}`
    }
  }
}

/**
 * Mask all characters except the first two
 * @param {string} value
 * @returns {string}
 * @example maskValue('Jens') // 'Je**'
 */
export const maskValue = <T extends string | undefined>(value: T): T => {
  if (!value) return value

  return (value.slice(0, 2) + '*'.repeat(Math.max(0, value.length - 2))) as T
}

/**
 * Mask everything but the first 5 characters of a phone number
 * @param {string} phone
 * @returns {string}
 * @example maskPhone('+32 474 123 456') // '+32 4** *** ***'
 */
export const maskPhone = <T extends string | undefined>(phone: T): T => {
  if (!phone) return phone

  return (phone.slice(0, 5) + phone.slice(5).replace(/\d/g, '*')) as T
}

/**
 * Mask parts of an email address
 * @param {string} email
 * @returns {string}
 * @example maskEmail('test@boltenergie.be') // 't**t@boltenergie.be'
 */
export const maskEmail = <T extends string | undefined>(email: T): T => {
  if (!email) return email

  return email.replace(/^(.)(.*)(.@.*)$/, (_, a, b, c) => a + b.replace(/./g, '*') + c) as T
}
